import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ScanLine, FileText, Upload, Camera, RefreshCw, Trash2,
  Loader2, CheckCircle2,
} from "lucide-react";
import AuthImg from "./AuthImg";
import { useAuth } from "@/lib/auth";

/**
 * CaseDocumentsList — compact list for Fahrzeugschein (Auto-Scan) + Unfallbericht.
 * Each row = one doc type. Whole row accepts drag&drop. Actions inline on the right.
 * Extracted data from Fahrzeugschein is bubbled up via onScanned so the parent
 * fills customer/vehicle fields directly — this component shows no result card.
 */
export default function CaseDocumentsList({ caseId, documents = [], onDocumentsChange, onScanned }) {
  const { accessToken } = useAuth();
  const [preview, setPreview] = useState(null);

  const fahrzeugscheine = (documents || []).filter((d) => d.doc_type === "fahrzeugschein");
  const unfallberichte = (documents || []).filter((d) => d.doc_type === "unfallbericht");

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="case-documents-list">
      <div className="px-5 py-4 border-b border-zinc-200 bg-zinc-50/50 flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg brand-gradient-bg flex items-center justify-center">
          <FileText className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold tracking-tight text-zinc-900">Dokumente zur Akte</div>
          <div className="text-xs text-zinc-500">Fahrzeugschein (Auto-Scan) und Unfallbericht</div>
        </div>
      </div>

      <div className="divide-y divide-zinc-200">
        <Row
          kind="fahrzeugschein"
          title="Fahrzeugschein"
          subtitle="Nur die Vorderseite — Halter- und Fahrzeugdaten werden automatisch ausgelesen."
          iconColor="bg-emerald-50 text-emerald-700"
          iconComponent={ScanLine}
          docs={fahrzeugscheine}
          caseId={caseId}
          accessToken={accessToken}
          onDocumentsChange={onDocumentsChange}
          documents={documents}
          onScanned={onScanned}
          onPreview={setPreview}
          showRescan
          acceptPdf={false}
        />
        <Row
          kind="unfallbericht"
          title="Unfallbericht"
          subtitle="Europäischer Unfallbericht / Schadenprotokoll — Bild oder PDF."
          iconColor="bg-cyan-50 text-cyan-700"
          iconComponent={FileText}
          docs={unfallberichte}
          caseId={caseId}
          accessToken={accessToken}
          onDocumentsChange={onDocumentsChange}
          documents={documents}
          onPreview={setPreview}
          showRescan={false}
          acceptPdf
        />
      </div>

      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="documents-lightbox"
        >
          <div className="max-w-4xl w-full bg-white rounded-xl p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
            {(preview.original_filename || "").toLowerCase().endsWith(".pdf") ? (
              <div className="h-[70vh] bg-zinc-100 rounded-lg flex flex-col items-center justify-center p-8 text-center gap-3">
                <FileText className="h-14 w-14 text-cyan-600" />
                <div className="font-display text-lg font-bold">{preview.original_filename}</div>
                <a
                  href={`/api/files/download/${encodeURI(preview.storage_path)}?auth=${accessToken || ""}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-cyan-700 hover:underline"
                >
                  PDF in neuem Tab öffnen
                </a>
              </div>
            ) : (
              <AuthImg path={preview.storage_path} token={accessToken} alt="" className="max-h-[70vh] w-full object-contain bg-zinc-100 rounded-lg" />
            )}
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>Schließen</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  kind, title, subtitle, iconColor, iconComponent: Icon,
  docs, caseId, accessToken, documents, onDocumentsChange, onScanned,
  onPreview, showRescan, acceptPdf,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const isPdf = (d) => (d.original_filename || "").toLowerCase().endsWith(".pdf");
  const latest = docs[docs.length - 1];
  const count = docs.length;
  const hasUploads = count > 0;

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      if (kind === "fahrzeugschein") {
        // scan + store per file
        const collected = [];
        for (const file of Array.from(fileList)) {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("case_id", caseId);
          const { data } = await api.post("/ai/scan-fahrzeugschein", fd, {
          });
          if (data.document) collected.push(data.document);
          onScanned?.(data);
          if (data.vin_needs_review) {
            toast.warning(
              `Fahrgestellnummer bitte prüfen! ${data.vin_review_note || "Die FIN konnte nicht zweifelsfrei gelesen werden."}`,
              { duration: 20000, id: "vin-review" }
            );
          }
          const filled =
            Object.values(data.customer || {}).filter(Boolean).length +
            Object.values(data.vehicle || {}).filter((v) => v !== null && v !== "").length;
          if (filled > 0) {
            toast.success(
              data.vin_verified
                ? `${filled} Felder ausgefüllt · FIN von 2 Quellen bestätigt ✓`
                : `${filled} Felder automatisch ausgefüllt`
            );
          } else {
            const hint = data.notes ? ` (${data.notes})` : "";
            toast.warning(
              `Kein Text erkannt${hint}. Bitte schärferes Foto bei guter Beleuchtung machen oder Felder manuell eintragen.`,
              { duration: 8000 }
            );
          }
        }
        if (collected.length) {
          onDocumentsChange?.([...(documents || []), ...collected]);
        }
      } else {
        const fd = new FormData();
        Array.from(fileList).forEach((f) => fd.append("files", f));
        fd.append("doc_type", "unfallbericht");
        const { data } = await api.post(`/files/cases/${caseId}/documents`, fd, {
        });
        onDocumentsChange?.([...(documents || []), ...data.documents]);
        toast.success(`${data.documents.length} Unfallbericht(e) hochgeladen`);
      }
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploading(false);
    }
  };

  const rescan = async (docId) => {
    setScanning(docId);
    try {
      const fd = new FormData();
      fd.append("case_id", caseId);
      fd.append("doc_id", docId);
      const { data } = await api.post("/ai/scan-fahrzeugschein/existing", fd, {
      });
      onScanned?.(data);
      if (data.vin_needs_review) {
        toast.warning(
          `Fahrgestellnummer bitte prüfen! ${data.vin_review_note || "Die FIN konnte nicht zweifelsfrei gelesen werden."}`,
          { duration: 20000, id: "vin-review" }
        );
      }
      const filled =
        Object.values(data.customer || {}).filter(Boolean).length +
        Object.values(data.vehicle || {}).filter((v) => v !== null && v !== "").length;
      toast.success(
        filled > 0
          ? `Erneut erkannt · ${filled} Felder aktualisiert${data.vin_verified ? " · FIN von 2 Quellen bestätigt ✓" : ""}`
          : "Keine Felder erkannt"
      );
    } catch (e) {
      toast.error(apiError(e, "Erneuter Scan fehlgeschlagen"));
    } finally {
      setScanning(null);
    }
  };

  const deleteDoc = async (docId) => {
    setDeleting(docId);
    try {
      await api.delete(`/files/cases/${caseId}/documents/${docId}`);
      onDocumentsChange?.((documents || []).filter((d) => d.id !== docId));
      toast.success("Dokument entfernt");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div
      className={`px-4 sm:px-6 py-5 sm:py-6 transition relative ${dragOver ? "bg-emerald-50/40" : "hover:bg-zinc-50/40"}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      data-testid={`docrow-${kind}`}
    >
      {dragOver && (
        <div className="absolute inset-2 rounded-xl border-2 border-dashed border-emerald-400 bg-emerald-50/70 flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <Upload className="h-5 w-5" />
            Jetzt loslassen
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 sm:gap-5">
        {/* Left slot: thumbnail if uploaded, otherwise the category icon */}
        {hasUploads ? (
          <button
            type="button"
            onClick={() => latest && onPreview(latest)}
            className="relative h-16 w-16 sm:h-20 sm:w-20 rounded-xl overflow-hidden border border-zinc-200 bg-zinc-100 shrink-0 group"
            title="Vorschau öffnen"
            data-testid={`docrow-${kind}-thumb`}
          >
            {isPdf(latest) ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-cyan-600">
                <FileText className="h-7 w-7" strokeWidth={1.8} />
                <span className="text-[9px] font-bold tracking-wider mt-0.5">PDF</span>
              </div>
            ) : (
              <AuthImg path={latest.storage_path} token={accessToken} alt={title} className="w-full h-full object-cover" />
            )}
            {count > 1 && (
              <span className="absolute -top-1.5 -right-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-white shadow">
                {count}
              </span>
            )}
          </button>
        ) : (
          <div className={`h-16 w-16 sm:h-20 sm:w-20 rounded-xl flex items-center justify-center shrink-0 ${iconColor}`}>
            <Icon className="h-9 w-9 sm:h-10 sm:w-10" strokeWidth={1.5} />
          </div>
        )}

        {/* Label */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-bold text-base sm:text-lg tracking-tight text-zinc-900">{title}</div>
            {hasUploads ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <CheckCircle2 className="h-3 w-3" strokeWidth={2.4} />
                geladen
              </span>
            ) : (
              <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5">
                leer
              </span>
            )}
          </div>
          <div className="text-xs sm:text-sm text-zinc-500 mt-1 leading-relaxed">{subtitle}</div>
        </div>

        {/* Actions — compact on mobile (icon-only), full text on ≥sm */}
        <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
          <input
            ref={inputRef}
            type="file"
            accept={acceptPdf ? "image/*,application/pdf" : "image/*"}
            multiple
            className="hidden"
            data-testid={`docrow-${kind}-input`}
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          />
          {hasUploads && showRescan && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold text-cyan-700 border-cyan-200 hover:bg-cyan-50"
              onClick={() => rescan(latest.id)}
              disabled={scanning === latest.id}
              data-testid={`docrow-${kind}-rescan`}
              title="Neu scannen"
            >
              {scanning === latest.id ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 sm:mr-1.5" />}
              <span className="hidden sm:inline">Neu scannen</span>
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            className={hasUploads
              ? "h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold bg-white border border-zinc-200 text-zinc-800 hover:bg-zinc-50"
              : "h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-semibold brand-gradient-bg text-white shadow-sm"}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid={`docrow-${kind}-upload`}
            title={hasUploads ? "Weiteres Foto" : "Fotografieren"}
          >
            {uploading ? <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">{hasUploads ? "Weitere" : "Fotografieren"}</span>
          </Button>
          {hasUploads && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-9 w-9 sm:h-10 sm:w-10 text-rose-600 hover:bg-rose-50"
              onClick={() => deleteDoc(latest.id)}
              disabled={deleting === latest.id}
              data-testid={`docrow-${kind}-delete`}
              title="Entfernen"
            >
              {deleting === latest.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          )}
        </div>
      </div>

      {/* Desktop-only hint about drag & drop */}
      <div className="hidden lg:flex items-center gap-1.5 text-[10px] text-zinc-400 mt-3 ml-[100px]">
        <Upload className="h-3 w-3" />
        Desktop: per Drag &amp; Drop in diese Zeile ablegen
      </div>
    </div>
  );
}
